import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, Tabs, Tab, Button, CircularProgress } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import FolderIcon from '@material-ui/icons/Folder';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import { fetchContentLogsDir, fetchContentLogFile } from '../../../redux/actions/contentLogsAction';
import { useStyles } from './style';
import AdminSideBar from '../Sidebar';

const CATEGORIES = ['emails', 'chat', 'posts'];
const TAB_LABELS = ['Emails', 'Chat', 'Posts'];

const ContentLogs: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { entries, currentFile, loading, error } = useSelector(
    (state: RootState) => state.contentLogsReducer
  );

  const [activeTab, setActiveTab] = useState(0);
  // pathSegments tracks navigation depth within a category, e.g. ['community-slug', 'channel-slug']
  const [pathSegments, setPathSegments] = useState<string[]>([]);
  const [viewingFile, setViewingFile] = useState(false);

  const currentCategory = CATEGORIES[activeTab];

  const buildApiPath = useCallback(
    (segments: string[]) => [currentCategory, ...segments].join('/'),
    [currentCategory]
  );

  // Fetch directory when tab or path changes
  useEffect(() => {
    if (!viewingFile) {
      dispatch(fetchContentLogsDir(buildApiPath(pathSegments)));
    }
  }, [dispatch, activeTab, pathSegments, viewingFile, buildApiPath]);

  const handleTabChange = (_: any, val: number) => {
    setActiveTab(val);
    setPathSegments([]);
    setViewingFile(false);
  };

  const handleEntryClick = (entry: any) => {
    if (entry.type === 'dir') {
      setPathSegments((prev) => [...prev, entry.name]);
    } else {
      // It's a file — read it
      const filePath = buildApiPath([...pathSegments, entry.name]);
      dispatch(fetchContentLogFile(filePath));
      setViewingFile(true);
    }
  };

  const handleBack = () => {
    if (viewingFile) {
      setViewingFile(false);
    } else if (pathSegments.length > 0) {
      setPathSegments((prev) => prev.slice(0, -1));
    }
  };

  const handleBreadcrumbClick = (index: number) => {
    // index -1 means root (category level)
    if (viewingFile) {
      setViewingFile(false);
    }
    setPathSegments((prev) => prev.slice(0, index + 1));
  };

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  const renderBreadcrumb = () => {
    const crumbs = [currentCategory, ...pathSegments];
    return (
      <Box className={classes.breadcrumb}>
        {crumbs.map((crumb, idx) => {
          const isLast = idx === crumbs.length - 1 && !viewingFile;
          return (
            <React.Fragment key={idx}>
              {idx > 0 && <span className={classes.breadcrumbSeparator}>/</span>}
              {isLast ? (
                <span className={classes.breadcrumbCurrent}>{crumb}</span>
              ) : (
                <span
                  className={classes.breadcrumbItem}
                  onClick={() => handleBreadcrumbClick(idx - 1)}
                >
                  {crumb}
                </span>
              )}
            </React.Fragment>
          );
        })}
        {viewingFile && currentFile && (
          <>
            <span className={classes.breadcrumbSeparator}>/</span>
            <span className={classes.breadcrumbCurrent}>{currentFile.filename}</span>
          </>
        )}
      </Box>
    );
  };

  const renderFileContent = () => {
    if (!currentFile) return null;

    const { content, contentType, filename } = currentFile;

    if (contentType === 'html') {
      return (
        <Box className={classes.fileViewer}>
          <Typography variant="subtitle2" style={{ marginBottom: 8, color: '#6B7280' }}>
            {filename}
          </Typography>
          <iframe
            className={classes.htmlFrame}
            srcDoc={content}
            sandbox="allow-same-origin"
            title={filename}
          />
        </Box>
      );
    }

    if (contentType === 'json') {
      let formatted = content;
      try {
        formatted = JSON.stringify(JSON.parse(content), null, 2);
      } catch {
        // already formatted or invalid
      }
      return (
        <Box className={classes.fileViewer}>
          <Typography variant="subtitle2" style={{ marginBottom: 8, color: '#6B7280' }}>
            {filename}
          </Typography>
          <pre className={classes.jsonContent}>{formatted}</pre>
        </Box>
      );
    }

    // Default: text
    return (
      <Box className={classes.fileViewer}>
        <Typography variant="subtitle2" style={{ marginBottom: 8, color: '#6B7280' }}>
          {filename}
        </Typography>
        <pre className={classes.preContent}>{content}</pre>
      </Box>
    );
  };

  const renderDirectoryListing = () => {
    if (loading) {
      return (
        <Box style={{ textAlign: 'center', padding: 40 }}>
          <CircularProgress size={32} />
        </Box>
      );
    }

    if (error) {
      return (
        <Box className={classes.emptyState}>
          <Typography color="error">{error}</Typography>
        </Box>
      );
    }

    if (!entries || entries.length === 0) {
      return (
        <Box className={classes.emptyState}>
          <Typography>No content logs found in this directory.</Typography>
        </Box>
      );
    }

    return (
      <Box>
        {entries.map((entry: any, idx: number) => (
          <Box
            key={idx}
            className={classes.entryRow}
            onClick={() => handleEntryClick(entry)}
          >
            {entry.type === 'dir' ? (
              <FolderIcon className={classes.entryIcon} style={{ color: '#F59E0B' }} />
            ) : (
              <InsertDriveFileIcon className={classes.entryIcon} />
            )}
            <span className={classes.entryName}>{entry.name}</span>
            <span className={classes.entryDate}>{formatDate(entry.modifiedAt)}</span>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <div className="wrapper">
      <AdminSideBar />
      <div className="main_cnt" style={{ minHeight: '100vh' }}>
        <Box className={classes.container}>
          <Box className={classes.header}>
            <Typography className={classes.title}>Content Logs</Typography>
          </Box>

          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            style={{ marginBottom: 16 }}
          >
            {TAB_LABELS.map((label) => (
              <Tab key={label} label={label} />
            ))}
          </Tabs>

          {renderBreadcrumb()}

          {(pathSegments.length > 0 || viewingFile) && (
            <Button
              className={classes.backButton}
              variant="outlined"
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
            >
              Back
            </Button>
          )}

          {viewingFile ? renderFileContent() : renderDirectoryListing()}
        </Box>
      </div>
    </div>
  );
};

export default ContentLogs;
